/**
 * EmptyState Component - Design System
 * Epic 1.4 - Empty States Premium & Product Polish
 * 
 * Universal component for all empty states across NEXORA AI
 * Supports multiple variants, sizes, and extensible props
 * 
 * @module components/ui/empty-state
 */

import React from 'react';
import { motion } from 'framer-motion';
import { Button } from './button';
import { Badge } from './badge';

/**
 * EmptyState Component
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.icon - Icon or component (Lucide icon recommended)
 * @param {string} props.title - User-oriented title (required)
 * @param {string} props.message - Value proposition message (required)
 * @param {Object} props.primaryAction - Primary CTA { label, onClick?, href?, icon? }
 * @param {Object} props.secondaryAction - Secondary CTA { label, onClick?, href?, variant?, icon? }
 * @param {'empty' | 'success' | 'error' | 'info'} props.variant - Visual variant
 * @param {string} props.illustration - Path to illustration image (alternative to icon)
 * @param {'top' | 'center' | 'background'} props.illustrationPosition - Illustration placement
 * @param {'compact' | 'default' | 'large'} props.size - Component size
 * @param {boolean} props.animated - Enable Framer Motion animations
 * @param {number} props.animationDelay - Delay before animation starts (seconds)
 * @param {string} props.testId - Test ID for E2E tests
 * @param {string} props.ariaLabel - ARIA label for accessibility
 * @param {React.ReactNode} props.children - Custom content slot
 * @param {React.ReactNode} props.footer - Footer content slot
 * @param {'Nouveau' | 'Bêta' | 'Premium' | 'Recommandé' | string} props.badge - Optional badge
 * @param {React.ReactNode} props.customActions - Custom actions slot (video, checklist, demo, multiple CTAs)
 */
export const EmptyState = ({
  icon,
  title,
  message,
  primaryAction,
  secondaryAction,
  variant = 'empty',
  illustration,
  illustrationPosition = 'top',
  size = 'default',
  animated = true,
  animationDelay = 0.2,
  testId = 'empty-state',
  ariaLabel,
  children,
  footer,
  badge,
  customActions,
}) => {
  // Variant color schemes
  const variantStyles = {
    empty: {
      iconBg: 'bg-blue-600/10',
      iconBorder: 'border-blue-500/20',
      iconText: 'text-blue-400',
      badgeBg: 'bg-blue-500',
    },
    success: {
      iconBg: 'bg-emerald-600/10',
      iconBorder: 'border-emerald-500/20',
      iconText: 'text-emerald-400',
      badgeBg: 'bg-emerald-500',
    },
    error: {
      iconBg: 'bg-red-600/10',
      iconBorder: 'border-red-500/20',
      iconText: 'text-red-400',
      badgeBg: 'bg-red-500',
    },
    info: {
      iconBg: 'bg-purple-600/10',
      iconBorder: 'border-purple-500/20',
      iconText: 'text-purple-400',
      badgeBg: 'bg-purple-500',
    },
  };

  // Size configurations
  const sizeConfig = {
    compact: {
      container: 'py-8 px-4',
      iconSize: 'w-10 h-10',
      iconWrapper: 'w-12 h-12',
      title: 'text-lg',
      message: 'text-sm',
      gap: 'space-y-3',
    },
    default: {
      container: 'py-12 px-6',
      iconSize: 'w-12 h-12',
      iconWrapper: 'w-16 h-16',
      title: 'text-xl',
      message: 'text-base',
      gap: 'space-y-4',
    },
    large: {
      container: 'py-20 px-8',
      iconSize: 'w-16 h-16',
      iconWrapper: 'w-20 h-20',
      title: 'text-2xl sm:text-3xl',
      message: 'text-base sm:text-lg',
      gap: 'space-y-6',
    },
  };

  const styles = variantStyles[variant];
  const sizeStyles = sizeConfig[size];

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        delay: animationDelay,
        ease: 'easeOut',
      },
    },
  };

  const iconVariants = {
    hidden: { scale: 0.8, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        duration: 0.4,
        delay: animationDelay + 0.1,
        ease: 'backOut',
      },
    },
  };

  const contentVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.4,
        delay: animationDelay + 0.2,
      },
    },
  };

  const Container = animated ? motion.div : 'div';
  const IconWrapper = animated ? motion.div : 'div';
  const Content = animated ? motion.div : 'div';

  const containerProps = animated
    ? {
        initial: 'hidden',
        animate: 'visible',
        variants: containerVariants,
      }
    : {};

  const iconProps = animated
    ? {
        initial: 'hidden',
        animate: 'visible',
        variants: iconVariants,
      }
    : {};

  const contentProps = animated
    ? {
        initial: 'hidden',
        animate: 'visible',
        variants: contentVariants,
      }
    : {};

  return (
    <Container
      data-testid={testId}
      aria-label={ariaLabel || title}
      className={`flex flex-col items-center justify-center text-center ${sizeStyles.container}`}
      {...containerProps}
    >
      {/* Badge */}
      {badge && (
        <motion.div
          initial={animated ? { opacity: 0, y: -10 } : {}}
          animate={animated ? { opacity: 1, y: 0 } : {}}
          transition={animated ? { delay: animationDelay, duration: 0.3 } : {}}
          className="mb-4"
        >
          <Badge className={`${styles.badgeBg} text-white border-0`}>
            {badge}
          </Badge>
        </motion.div>
      )}

      {/* Icon or Illustration */}
      {illustration ? (
        <IconWrapper
          className={`mb-6 ${illustrationPosition === 'background' ? 'absolute opacity-10' : ''}`}
          {...iconProps}
        >
          <img
            src={illustration}
            alt=""
            className={`${sizeStyles.iconWrapper} object-contain`}
            loading="lazy"
          />
        </IconWrapper>
      ) : icon ? (
        <IconWrapper
          className={`${sizeStyles.iconWrapper} rounded-2xl ${styles.iconBg} border ${styles.iconBorder} flex items-center justify-center mb-6`}
          {...iconProps}
        >
          <div className={`${sizeStyles.iconSize} ${styles.iconText} flex items-center justify-center`}>
            {icon}
          </div>
        </IconWrapper>
      ) : null}

      {/* Content */}
      <Content className={`${sizeStyles.gap} max-w-2xl`} {...contentProps}>
        {/* Title */}
        <h3 className={`${sizeStyles.title} font-bold text-white`}>
          {title}
        </h3>

        {/* Message */}
        <p className={`${sizeStyles.message} text-zinc-400 leading-relaxed`}>
          {message}
        </p>

        {/* Custom Content Slot */}
        {children && (
          <div className="mt-4">
            {children}
          </div>
        )}

        {/* Custom Actions Slot (new) */}
        {customActions ? (
          <div className="mt-6">
            {customActions}
          </div>
        ) : (
          /* Default Actions */
          (primaryAction || secondaryAction) && (
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-6">
              {primaryAction && (
                <Button
                  onClick={primaryAction.onClick}
                  asChild={!!primaryAction.href}
                  className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white shadow-lg shadow-blue-600/30 transition-all"
                  data-testid={`${testId}-primary-action`}
                >
                  {primaryAction.href ? (
                    <a href={primaryAction.href} className="flex items-center gap-2">
                      {primaryAction.icon}
                      <span>{primaryAction.label}</span>
                    </a>
                  ) : (
                    <span className="flex items-center gap-2">
                      {primaryAction.icon}
                      <span>{primaryAction.label}</span>
                    </span>
                  )}
                </Button>
              )}

              {secondaryAction && (
                <Button
                  onClick={secondaryAction.onClick}
                  asChild={!!secondaryAction.href}
                  variant={secondaryAction.variant || 'outline'}
                  className="w-full sm:w-auto"
                  data-testid={`${testId}-secondary-action`}
                >
                  {secondaryAction.href ? (
                    <a href={secondaryAction.href} className="flex items-center gap-2">
                      {secondaryAction.icon}
                      <span>{secondaryAction.label}</span>
                    </a>
                  ) : (
                    <span className="flex items-center gap-2">
                      {secondaryAction.icon}
                      <span>{secondaryAction.label}</span>
                    </span>
                  )}
                </Button>
              )}
            </div>
          )
        )}

        {/* Footer Slot */}
        {footer && (
          <div className="mt-6 pt-6 border-t border-zinc-800">
            {footer}
          </div>
        )}
      </Content>
    </Container>
  );
};

export default EmptyState;
